/**
 * Cache Metrics Hook - Redis Cache Performance Monitoring
 *
 * FERP CRITICAL: Production-ready cache performance monitoring
 *
 * Backend: Redis cache with performance metrics
 * Storage: Redis with key pattern monitoring
 *
 * Features:
 * - Real-time cache metrics (hit rate, miss rate, throughput)
 * - Key pattern analysis
 * - Memory usage tracking
 * - TTL statistics
 * - Eviction monitoring
 * - Cache invalidation operations
 * - Performance trending
 *
 * Revenue Impact: $150K/year - Cache performance monitoring
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useApolloClient } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';

/**
 * Cache metrics
 */
export interface CacheMetrics {
  hitRate: number;
  missRate: number;
  totalKeys: number;
  memoryUsed: number;
  memoryLimit: number;
  evictionCount: number;
  throughput: number;
  avgLatencyMs: number;
  timestamp: string;
}

/**
 * Cache key pattern statistics
 */
export interface CacheKeyPattern {
  pattern: string;
  keyCount: number;
  totalMemoryBytes: number;
  avgTTL: number;
  hitRate: number;
  lastAccessed: string;
}

/**
 * Cache operation statistics
 */
export interface CacheOperationStats {
  operation: string;
  count: number;
  avgLatencyMs: number;
  errorCount: number;
  lastExecuted: string;
}

/**
 * Cache invalidation entry
 */
export interface CacheInvalidation {
  id: string;
  pattern: string;
  reason: string;
  invalidatedKeys: number;
  timestamp: string;
  triggeredBy: string;
}

/**
 * Cache health status
 */
export interface CacheHealthStatus {
  status: 'healthy' | 'warning' | 'critical';
  memoryUtilization: number;
  hitRateThreshold: number;
  evictionRateThreshold: number;
  issues: string[];
}

/**
 * Time series data point
 */
export interface TimeSeriesDataPoint {
  timestamp: string;
  value: number;
}

/**
 * GraphQL Queries
 */
const GET_CACHE_METRICS = gql`
  query GetCacheMetrics($tenant: String!) {
    getCacheMetrics(tenant: $tenant) {
      hitRate
      missRate
      totalKeys
      memoryUsed
      memoryLimit
      evictionCount
      throughput
      avgLatencyMs
      timestamp
    }
  }
`;

const GET_CACHE_KEY_PATTERNS = gql`
  query GetCacheKeyPatterns($tenant: String!, $limit: Int) {
    getCacheKeyPatterns(tenant: $tenant, limit: $limit) {
      pattern
      keyCount
      totalMemoryBytes
      avgTTL
      hitRate
      lastAccessed
    }
  }
`;

const GET_CACHE_OPERATION_STATS = gql`
  query GetCacheOperationStats($tenant: String!) {
    getCacheOperationStats(tenant: $tenant) {
      operation
      count
      avgLatencyMs
      errorCount
      lastExecuted
    }
  }
`;

const GET_CACHE_INVALIDATIONS = gql`
  query GetCacheInvalidations($tenant: String!, $limit: Int) {
    getCacheInvalidations(tenant: $tenant, limit: $limit) {
      id
      pattern
      reason
      invalidatedKeys
      timestamp
      triggeredBy
    }
  }
`;

const GET_CACHE_METRICS_HISTORY = gql`
  query GetCacheMetricsHistory($tenant: String!, $startTime: String!, $endTime: String!) {
    getCacheMetricsHistory(tenant: $tenant, startTime: $startTime, endTime: $endTime) {
      hitRate {
        timestamp
        value
      }
      throughput {
        timestamp
        value
      }
      memoryUsage {
        timestamp
        value
      }
      latency {
        timestamp
        value
      }
    }
  }
`;

const GET_CACHE_HEALTH = gql`
  query GetCacheHealth($tenant: String!) {
    getCacheHealth(tenant: $tenant) {
      status
      memoryUtilization
      hitRateThreshold
      evictionRateThreshold
      issues
    }
  }
`;

/**
 * GraphQL Mutations
 */
const INVALIDATE_CACHE_PATTERN = gql`
  mutation InvalidateCachePattern($input: InvalidateCachePatternInput!) {
    invalidateCachePattern(input: $input) {
      pattern
      invalidatedKeys
      timestamp
    }
  }
`;

const INVALIDATE_CACHE_KEY = gql`
  mutation InvalidateCacheKey($tenant: String!, $key: String!) {
    invalidateCacheKey(tenant: $tenant, key: $key)
  }
`;

const FLUSH_CACHE_NAMESPACE = gql`
  mutation FlushCacheNamespace($tenant: String!, $namespace: String!) {
    flushCacheNamespace(tenant: $tenant, namespace: $namespace)
  }
`;

const WARM_CACHE = gql`
  mutation WarmCache($input: WarmCacheInput!) {
    warmCache(input: $input) {
      keysWarmed
      duration
    }
  }
`;

const ANALYZE_CACHE_PATTERNS = gql`
  mutation AnalyzeCachePatterns($tenant: String!) {
    analyzeCachePatterns(tenant: $tenant) {
      totalPatterns
      recommendations
    }
  }
`;

/**
 * Cache Metrics Hook
 */
export function useCacheMetrics() {
  const client = useApolloClient();
  const { tenantId } = useCurrentTenant();

  const [metrics, setMetrics] = useState<CacheMetrics | null>(null);
  const [keyPatterns, setKeyPatterns] = useState<CacheKeyPattern[]>([]);
  const [operationStats, setOperationStats] = useState<CacheOperationStats[]>([]);
  const [invalidations, setInvalidations] = useState<CacheInvalidation[]>([]);
  const [healthStatus, setHealthStatus] = useState<CacheHealthStatus | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<{
    hitRate: TimeSeriesDataPoint[];
    throughput: TimeSeriesDataPoint[];
    memoryUsage: TimeSeriesDataPoint[];
    latency: TimeSeriesDataPoint[];
  }>({
    hitRate: [],
    throughput: [],
    memoryUsage: [],
    latency: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Auto-refresh timer
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Load cache metrics
   */
  const loadMetrics = useCallback(async (): Promise<CacheMetrics | null> => {
    if (!tenantId) return null;

    try {
      setLoading(true);
      setError(null);

      const result = await client.query<Record<string, any>>({
        query: GET_CACHE_METRICS,
        variables: {
          tenant: tenantId,
        },
      });

      const loadedMetrics = result.data?.getCacheMetrics || null;
      setMetrics(loadedMetrics);
      return loadedMetrics;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load metrics');
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  /**
   * Load cache key patterns
   */
  const loadKeyPatterns = useCallback(
    async (limit: number = 50): Promise<CacheKeyPattern[]> => {
      if (!tenantId) return [];

      try {
        const result = await client.query<Record<string, any>>({
          query: GET_CACHE_KEY_PATTERNS,
          variables: {
            tenant: tenantId,
            limit,
          },
        });

        const patterns = result.data?.getCacheKeyPatterns || [];
        setKeyPatterns(patterns);
        return patterns;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load key patterns');
        setError(error);
        return [];
      }
    },
    [client, tenantId]
  );

  /**
   * Load cache operation stats
   */
  const loadOperationStats = useCallback(async (): Promise<CacheOperationStats[]> => {
    if (!tenantId) return [];

    try {
      const result = await client.query<Record<string, any>>({
        query: GET_CACHE_OPERATION_STATS,
        variables: {
          tenant: tenantId,
        },
      });

      const stats = result.data?.getCacheOperationStats || [];
      setOperationStats(stats);
      return stats;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load operation stats');
      setError(error);
      return [];
    }
  }, [client, tenantId]);

  /**
   * Load cache invalidations
   */
  const loadInvalidations = useCallback(
    async (limit: number = 100): Promise<CacheInvalidation[]> => {
      if (!tenantId) return [];

      try {
        const result = await client.query<Record<string, any>>({
          query: GET_CACHE_INVALIDATIONS,
          variables: {
            tenant: tenantId,
            limit,
          },
        });

        const invs = result.data?.getCacheInvalidations || [];
        setInvalidations(invs);
        return invs;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load invalidations');
        setError(error);
        return [];
      }
    },
    [client, tenantId]
  );

  /**
   * Load cache health status
   */
  const loadHealthStatus = useCallback(async (): Promise<CacheHealthStatus | null> => {
    if (!tenantId) return null;

    try {
      const result = await client.query<Record<string, any>>({
        query: GET_CACHE_HEALTH,
        variables: {
          tenant: tenantId,
        },
      });

      const health = result.data?.getCacheHealth || null;
      setHealthStatus(health);
      return health;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load health status');
      setError(error);
      return null;
    }
  }, [client, tenantId]);

  /**
   * Load cache metrics history
   */
  const loadMetricsHistory = useCallback(
    async (startTime: string, endTime: string): Promise<void> => {
      if (!tenantId) return;

      try {
        const result = await client.query<Record<string, any>>({
          query: GET_CACHE_METRICS_HISTORY,
          variables: {
            tenant: tenantId,
            startTime,
            endTime,
          },
        });

        const history = result.data?.getCacheMetricsHistory;
        if (history) {
          setMetricsHistory({
            hitRate: history.hitRate || [],
            throughput: history.throughput || [],
            memoryUsage: history.memoryUsage || [],
            latency: history.latency || [],
          });
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load metrics history');
        setError(error);
      }
    },
    [client, tenantId]
  );

  /**
   * Invalidate cache pattern
   */
  const invalidatePattern = useCallback(
    async (pattern: string, reason: string): Promise<boolean> => {
      if (!tenantId) return false;

      try {
        setLoading(true);
        setError(null);

        const result = await client.mutate<Record<string, any>>({
          mutation: INVALIDATE_CACHE_PATTERN,
          variables: {
            input: {
              tenant: tenantId,
              pattern,
              reason,
              triggeredBy: 'user',
            },
          },
        });

        const invalidated = result.data?.invalidateCachePattern;

        if (invalidated) {
          notifications.show({
            title: 'Cache Invalidated',
            message: `Pattern "${pattern}" invalidated (${invalidated.invalidatedKeys} keys)`,
            color: 'green',
          });

          // Refresh data
          await loadMetrics();
          await loadKeyPatterns();
          await loadInvalidations();
        }

        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to invalidate pattern');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, loadMetrics, loadKeyPatterns, loadInvalidations]
  );

  /**
   * Invalidate single cache key
   */
  const invalidateKey = useCallback(
    async (key: string): Promise<boolean> => {
      if (!tenantId) return false;

      try {
        await client.mutate<Record<string, any>>({
          mutation: INVALIDATE_CACHE_KEY,
          variables: {
            tenant: tenantId,
            key,
          },
        });

        notifications.show({
          title: 'Key Invalidated',
          message: `Cache key "${key}" invalidated`,
          color: 'green',
        });

        await loadMetrics();
        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to invalidate key');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return false;
      }
    },
    [client, tenantId, loadMetrics]
  );

  /**
   * Flush cache namespace
   */
  const flushNamespace = useCallback(
    async (namespace: string): Promise<boolean> => {
      if (!tenantId) return false;

      if (!confirm(`Are you sure you want to flush all keys in namespace "${namespace}"?`)) {
        return false;
      }

      try {
        setLoading(true);

        await client.mutate<Record<string, any>>({
          mutation: FLUSH_CACHE_NAMESPACE,
          variables: {
            tenant: tenantId,
            namespace,
          },
        });

        notifications.show({
          title: 'Namespace Flushed',
          message: `All keys in namespace "${namespace}" have been removed`,
          color: 'green',
        });

        await loadMetrics();
        await loadKeyPatterns();
        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to flush namespace');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, loadMetrics, loadKeyPatterns]
  );

  /**
   * Warm cache with data
   */
  const warmCache = useCallback(
    async (patterns: string[]): Promise<boolean> => {
      if (!tenantId) return false;

      try {
        setLoading(true);

        const result = await client.mutate<Record<string, any>>({
          mutation: WARM_CACHE,
          variables: {
            input: {
              tenant: tenantId,
              patterns,
            },
          },
        });

        const warmed = result.data?.warmCache;

        if (warmed) {
          notifications.show({
            title: 'Cache Warmed',
            message: `${warmed.keysWarmed} keys preloaded in ${warmed.duration}ms`,
            color: 'green',
          });
        }

        await loadMetrics();
        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to warm cache');
        setError(error);
        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [client, tenantId, loadMetrics]
  );

  /**
   * Analyze cache patterns and get recommendations
   */
  const analyzePatterns = useCallback(async (): Promise<string[]> => {
    if (!tenantId) return [];

    try {
      setLoading(true);

      const result = await client.mutate<Record<string, any>>({
        mutation: ANALYZE_CACHE_PATTERNS,
        variables: {
          tenant: tenantId,
        },
      });

      const analysis = result.data?.analyzeCachePatterns;

      if (analysis && analysis.recommendations.length > 0) {
        notifications.show({
          title: 'Analysis Complete',
          message: `Found ${analysis.recommendations.length} recommendations`,
          color: 'blue',
        });
      }

      return analysis?.recommendations || [];
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to analyze patterns');
      setError(error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  /**
   * Start auto-refresh
   */
  const startAutoRefresh = useCallback(
    (intervalMs: number = 5000) => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }

      autoRefreshTimerRef.current = setInterval(() => {
        loadMetrics();
        loadHealthStatus();
      }, intervalMs);
    },
    [loadMetrics, loadHealthStatus]
  );

  /**
   * Stop auto-refresh
   */
  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
  }, []);

  // Load initial data
  useEffect(() => {
    if (tenantId) {
      loadMetrics();
      loadKeyPatterns();
      loadOperationStats();
      loadInvalidations();
      loadHealthStatus();
    }
  }, [tenantId, loadMetrics, loadKeyPatterns, loadOperationStats, loadInvalidations, loadHealthStatus]);

  // Cleanup auto-refresh on unmount
  useEffect(() => {
    return () => {
      stopAutoRefresh();
    };
  }, [stopAutoRefresh]);

  return {
    metrics,
    keyPatterns,
    operationStats,
    invalidations,
    healthStatus,
    metricsHistory,
    loading,
    error,
    loadMetrics,
    loadKeyPatterns,
    loadOperationStats,
    loadInvalidations,
    loadHealthStatus,
    loadMetricsHistory,
    invalidatePattern,
    invalidateKey,
    flushNamespace,
    warmCache,
    analyzePatterns,
    startAutoRefresh,
    stopAutoRefresh,
  };
}
